import React, { useState } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { auth } from "@/shared/api/graphqlClient";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Key,
  Power,
  PowerOff,
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Eye,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import MerchantForm from "@/shared/components/merchants/MerchantForm";

export default function Merchants() {
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const { t } = useLanguage();

  const statusConfig = {
    ACTIVE: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: t("active") },
    active: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: t("active") },
    INACTIVE: { icon: Clock, color: "bg-amber-100 text-amber-700", label: t("inactive") },
    pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: t("pending") },
    SUSPENDED: { icon: AlertTriangle, color: "bg-red-100 text-red-700", label: t("suspended") },
    suspended: { icon: AlertTriangle, color: "bg-red-100 text-red-700", label: t("suspended") },
    terminated: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: t("terminated") },
  };

  const queryClient = useQueryClient();

  const { data: merchants = [], isLoading, error } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => adminService.getAllMerchants(),
    enabled: auth.isAuthenticated(),
    retry: 2,
    refetchOnMount: true,
    staleTime: 0,
  });

  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => adminService.createMerchant(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["merchants"]);
      setFormOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminService.updateMerchant(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["merchants"]);
      setFormOpen(false);
      setSelectedMerchant(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminService.deleteMerchant(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["merchants"]);
      setDeleteDialog(null);
    },
  });

  const filteredMerchants = merchants.filter(
    (m) =>
      m.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.code?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async (data) => {
    if (selectedMerchant) {
      await updateMutation.mutateAsync({ id: selectedMerchant.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleEdit = (merchant) => {
    setSelectedMerchant(merchant);
    setFormOpen(true);
  };

  const handleToggleStatus = async (merchant) => {
    const currentStatus = merchant.status?.toUpperCase();
    const newStatus = currentStatus === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    await updateMutation.mutateAsync({ id: merchant.id, data: { name: merchant.name, status: newStatus } });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("merchants")}</h1>
          <p className="text-slate-500">
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </span>
            ) : (
              `${merchants.length} ${t("merchantsRegistered")}`
            )}
          </p>
        </div>
        <Button onClick={() => setFormOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          {t("addMerchant")}
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <p className="font-medium">Error loading merchants</p>
          <p className="text-sm">{error.message || "Failed to fetch merchants"}</p>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t("searchMerchants")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">{t("merchant")}</TableHead>
              <TableHead className="font-semibold">{t("code")}</TableHead>
              <TableHead className="font-semibold">{t("parent")}</TableHead>
              <TableHead className="font-semibold">{t("status")}</TableHead>
              <TableHead className="font-semibold">{t("features")}</TableHead>
              <TableHead className="font-semibold">{t("fee")}</TableHead>
              <TableHead className="font-semibold">{t("settlement")}</TableHead>
              <TableHead className="font-semibold w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <div className="flex flex-col items-center gap-3 text-slate-500">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                    <p>{t("loading")}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredMerchants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                  {merchants.length === 0 ? t("noMerchantsFound") : t("noMerchantsMatchSearch")}
                </TableCell>
              </TableRow>
            ) : (
              filteredMerchants.map((merchant, index) => {
                const status = statusConfig[merchant.status] || statusConfig.pending;
                const StatusIcon = status.icon;

                return (
                  <motion.tr
                    key={merchant.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.02 * index }}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{merchant.name}</p>
                          <p className="text-sm text-slate-500">{merchant.contact_email || t("noEmail")}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        {merchant.code}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        if (merchant.createdByResellerId) {
                          const reseller = resellers.find(r => r.id === merchant.createdByResellerId);
                          return (
                            <div className="text-sm">
                              <Badge variant="outline" className="border-purple-300 text-purple-700 bg-purple-50">
                                Reseller: {reseller?.name || merchant.createdByResellerId.substring(0, 8)}
                              </Badge>
                            </div>
                          );
                        }
                        if (merchant.createdByEngineId) {
                          const engine = engines.find(e => e.id === merchant.createdByEngineId);
                          return (
                            <div className="text-sm">
                              <Badge variant="outline" className="border-sky-300 text-sky-700 bg-sky-50">
                                Engine: {engine?.name || merchant.createdByEngineId.substring(0, 8)}
                              </Badge>
                            </div>
                          );
                        }
                        if (merchant.createdByAdminId) {
                          return (
                            <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50">
                              {t("directAdmin")}
                            </Badge>
                          );
                        }
                        return <span className="text-xs text-slate-400">Unknown</span>;
                      })()}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${status.color} gap-1 border-0`}>
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {merchant.deposit_enabled && (
                          <Badge variant="outline" className="text-xs">{t("deposit")}</Badge>
                        )}
                        {merchant.withdrawal_enabled && (
                          <Badge variant="outline" className="text-xs">{t("withdrawal")}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {merchant.fee_value}
                        {merchant.fee_type === "percentage" ? "%" : " THB"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">{merchant.settlement_period}</span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={createPageUrl("AdminMerchantDetail") + `?id=${merchant.id}`}>
                              <Eye className="h-4 w-4 mr-2" />
                              {t("viewDetails")}
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(merchant)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            {t("edit")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(merchant)}>
                            <Key className="h-4 w-4 mr-2" />
                            {t("viewApiKeys")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleToggleStatus(merchant)}>
                            {merchant.status?.toUpperCase() === "ACTIVE" ? (
                              <>
                                <PowerOff className="h-4 w-4 mr-2" />
                                {t("suspend")}
                              </>
                            ) : (
                              <>
                                <Power className="h-4 w-4 mr-2" />
                                {t("activate")}
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteDialog(merchant)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t("delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </motion.tr>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Merchant Form Dialog */}
      <MerchantForm
        merchant={selectedMerchant}
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedMerchant(null);
        }}
        onSave={handleSave}
        engines={engines}
        resellers={resellers}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteMerchant")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteConfirmation")} "{deleteDialog?.name}"? {t("cannotBeUndone")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate(deleteDialog.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}